import React, { useState, useEffect, useRef } from 'react';
import { PREDEFINED_INDUSTRIES } from '../../utils/constants';



const ProfessionalDetails = ({
    formData,
    handleInputChange,
    handleCoordinatorContactChange,
    validateCoordinatorContactOnBlur,
    industryInput,
    setIndustryInput,
    handleAddIndustry,
    handleAddIndustryBlur,
    handleRemoveIndustry,
    errors,
    validatedFields
}) => {
    const [filteredIndustries, setFilteredIndustries] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [limitError, setLimitError] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (industryInput) {
            const filtered = PREDEFINED_INDUSTRIES.filter(ind =>
                ind.toLowerCase().includes(industryInput.toLowerCase()) &&
                !formData.industry?.includes(ind)
            );
            setFilteredIndustries(filtered);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [industryInput, formData.industry]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const handleSelectIndustry = (industry) => {
        const currentIndustries = formData.industry || [];
        // Prevent duplicates
        if (!currentIndustries.includes(industry)) {
            // Update parent formData
            handleInputChange({
                target: {
                    name: 'industry',
                    value: [...currentIndustries, industry]
                }
            });
        }
        setIndustryInput('');
        setShowSuggestions(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const val = industryInput.trim();
            if (!val) return;

            setLimitError('');

            // Check if matches any predefined (case-insensitive)
            const match = PREDEFINED_INDUSTRIES.find(
                ind => ind.toLowerCase() === val.toLowerCase()
            );

            const industryToAdd = match || val;
            const currentIndustries = formData.industry || [];

            if (currentIndustries.includes(industryToAdd)) {
                setIndustryInput('');
                setShowSuggestions(false);
                return;
            }

            // If it's custom (no match), check limit
            if (!match) {
                // Count existing custom industries
                const customCount = currentIndustries.filter(
                    ind => !PREDEFINED_INDUSTRIES.includes(ind)
                ).length;

                if (customCount >= 5) {
                    setLimitError('You can only add custom industries up to 5 max');
                    return;
                }
            }

            // Add
            handleInputChange({
                target: {
                    name: 'industry',
                    value: [...currentIndustries, industryToAdd]
                }
            });
            setIndustryInput('');
            setShowSuggestions(false);
        }
    };

    return (
        <>
            <div className="form-section">
                <h3 className="section-heading">Professional Details</h3>
                <div className="row">
                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label className="form-label">Business Type</label>
                            <select
                                name="business_type"
                                value={formData.business_type}
                                onChange={handleInputChange}
                                className="form-input"
                            >
                                <option value="">Select Business Type</option>
                                <option value="Wholesaler">Wholesaler</option>
                                <option value="Manufacturer">Manufacturer</option>
                                <option value="Retailer">Retailer</option>
                                <option value="Professional">Professional</option>
                            </select>
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label className="form-label">Designation</label>
                            <select
                                name="designation"
                                value={formData.designation}
                                onChange={handleInputChange}
                                className="form-input"
                            >
                                <option value="">Select Designation</option>
                                <option value="Owner">Owner</option>
                                <option value="Partner">Partner</option>
                                <option value="Sr. Management">Sr. Management</option>
                                <option value="Other">Other</option>
                            </select>
                            {formData.designation === 'Other' && (
                                <input
                                    type="text"
                                    name="other_designation"
                                    placeholder="Enter Designation"
                                    value={formData.other_designation || ''}
                                    onChange={handleInputChange}
                                    className={`form-input mt-2${errors.other_designation ? ' error' : ''}`}
                                />
                            )}
                            {errors.other_designation && <span className="error-message">{errors.other_designation}</span>}
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="form-group" ref={wrapperRef} style={{ position: 'relative' }}>
                            <label className="form-label">Industry</label>
                            <input
                                type="text"
                                value={industryInput}
                                onChange={(e) => {
                                    setIndustryInput(e.target.value);
                                    if (limitError) setLimitError('');
                                }}
                                onKeyDown={handleKeyDown}
                                onFocus={() => industryInput && setShowSuggestions(true)}
                                placeholder="Type to search or enter to add custom industry"
                                className="form-input"
                                autoComplete="off"
                            />
                            {showSuggestions && filteredIndustries.length > 0 && (
                                <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                    {filteredIndustries.map((item, index) => (
                                        <li
                                            key={index}
                                            className="list-group-item list-group-item-action cursor-pointer"
                                            onClick={() => handleSelectIndustry(item)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="selected-locations mt-2 d-flex flex-wrap gap-2">
                                {Array.isArray(formData.industry) && formData.industry.map((ind, index) => (
                                    <span key={index} className="badge bg-light text-dark border p-2 d-flex align-items-center">
                                        {ind}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveIndustry(index)}
                                            className="btn-close ms-2"
                                            style={{ fontSize: '0.5rem' }}
                                            aria-label="Close"
                                        ></button>
                                    </span>
                                ))}
                            </div>

                            {limitError && <div className="text-danger mt-1" style={{ fontSize: '0.85rem' }}>{limitError}</div>}
                        </div>
                    </div>

                </div>
            </div>

            <div className="form-section">
                <h3 className="section-heading">Coordinator Contact</h3>
                <div className="row">
                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label className="form-label">Name</label>
                            <input
                                type="text"
                                name="coordinator_name"
                                placeholder="Enter coordinator name"
                                value={formData.coordinator_name}
                                onChange={handleInputChange}
                                className="form-input"
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label className="form-label">Contact No</label>
                            <div className="input-with-validation">
                                <input
                                    type="tel"
                                    name="coordinator_contact_no"
                                    placeholder="Enter 10 digit contact"
                                    value={formData.coordinator_contact_no}
                                    onChange={handleCoordinatorContactChange}
                                    onBlur={validateCoordinatorContactOnBlur}
                                    className={`form-input${errors.coordinator_contact_no ? ' error' : ''} ${validatedFields.coordinator_contact_no ? ' validated' : ''}`}
                                    inputMode="numeric"
                                    maxLength={10}
                                />
                                {validatedFields.coordinator_contact_no && <span className="validation-check">✓</span>}
                            </div>
                            {errors.coordinator_contact_no && <span className="error-message">{errors.coordinator_contact_no}</span>}
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3 className="section-heading">Nominee Details</h3>
                <div className="row">
                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label className="form-label">Name</label>
                            <input
                                type="text"
                                name="nominee_name"
                                placeholder="Enter nominee name"
                                value={formData.nominee_name}
                                onChange={handleInputChange}
                                className="form-input"
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label className="form-label">Relation</label>
                            <input
                                type="text"
                                name="nominee_relation"
                                placeholder="e.g., Spouse, Father, Mother"
                                value={formData.nominee_relation}
                                onChange={handleInputChange}
                                className="form-input"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3 className="section-heading">Other Info</h3>
                <div className="row">
                    <div className="col-12">
                        <div className="form-group">
                            <textarea
                                name="other_info"
                                placeholder="Enter any additional information"
                                value={formData.other_info}
                                onChange={handleInputChange}
                                className="form-input form-textarea"
                                rows="4"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProfessionalDetails;
